import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { Grid, TextField, Typography, Tooltip, FormControlLabel, FormHelperText, Checkbox, Switch, Dialog } from '@material-ui/core';
import { useFormik } from 'formik';
import Modal from 'components/Modal';
import Input from 'components/Input';
import Select from 'components/Select';
import Button from 'components/Button';
import WarningMessage from 'components/WarningMessage';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import { forceVehicleExit } from '../../store/thunk';
import { hoursAndMinutesToMinutes } from 'helpers/converters';

import { validations, modalValidations } from './validations';

export default function VehicleExit() {
  const dispatch = useDispatch();

  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { selects } = useSelector(state => state.filters);
  const { isLoading } = useSelector(state => state.advancedConfigs);

  const paymentMethods = selects
    ?.find(filter => filter?.id == 'paymentMethod')
    ?.options;

  const entryType = [
    { value: 0, label: 'Apenas saída nos veículos' },
    { value: 1, label: 'Dar saída e dar entrada nos veículos' }
  ];

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validationSchema: validations,
    initialValues: {
      startDate: moment(moment.now()).format('YYYY-MM-DD'),
      endDate: moment(moment.now()).format('YYYY-MM-DD'),
      endTime: '00:00',
      paymentMethodId: '',
      entryType: '',
      exitWithAmount: 0
    },
    onSubmit: () => setIsModalOpen(true)
  });

  const modalFormik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validationSchema: modalValidations,
    initialValues: {
      reason: '',
      password: '',
      passwordConfirm: '',
      actionConsent: false
    },
    onSubmit: values => {
      const params = {
        reason: values?.reason,
        userPassword: values?.password,
        startDateTime: `${moment(formik.values.startDate, 'YYYY-MM-DD').format('YYYY-MM-DD')} 00:00:00`,
        endDateTime: `${moment(formik.values.endDate, 'YYYY-MM-DD').format('YYYY-MM-DD')} ${formik.values.endTime}:00`,
        exitDateTime: `${moment(formik.values.endDate, 'YYYY-MM-DD').format('YYYY-MM-DD')} ${formik.values.endTime}:00`,
        paymentMethodId: formik?.values?.paymentMethodId,
        open: formik?.values?.entryType,
        exitWithAmount: formik?.values?.exitWithAmount
      }

      dispatch(forceVehicleExit(userId, establishmentId, params)).then(() => {
        setIsModalOpen(false);
        setIsSuccessModalOpen(true);
      });
    }
  });

  return(
    <Grid container>
      {isSuccessModalOpen && (
        <Modal
          maxWidth='sm'
          title='Operação realizada'
          open={isSuccessModalOpen}
          onClose={() => setIsSuccessModalOpen(false)}
        >
          <Typography>
            Dependendo do período escolhido a operação pode demorar até 24hrs para ser realizada em todas as OSs.
          </Typography>
        </Modal>
      )}
      {isModalOpen && (
        <Dialog
          id='vehicle-exit-advanced-config-modal'
          scroll='body'
          maxWidth='lg'
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        >
          <Grid container spacing={2} style={{ padding: 30 }}>
            <Grid item xs={12}>
              <WarningMessage
                justifyContent="flex-start"
                message="Essa ação não poderá ser desfeita!"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography>
                Confirmo que vou dar <b>{entryType.find(type => +type.value == +formik.values.entryType)?.label}</b> que tiveram entradas até o dia <b>{moment(formik.values.endDate, 'YYYY-MM-DD').format('DD/MM/YYYY')} {formik.values.endTime}</b> com o meio de pagamento <b>{paymentMethods?.find(paymentMethod => paymentMethod?.value == formik.values.paymentMethodId)?.label || ''}</b>
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <form onSubmit={modalFormik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={8}>
                    <Input
                      name="reason"
                      type="text"
                      label="Motivo"
                      value={modalFormik.values.reason}
                      error={modalFormik.errors.reason}
                      helperText={modalFormik.errors.reason}
                      onChange={modalFormik.handleChange}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Input
                      name="password"
                      type="password"
                      label="Informe sua senha"
                      value={modalFormik.values.password}
                      error={modalFormik.errors.password}
                      helperText={modalFormik.errors.password}
                      onChange={modalFormik.handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Input
                      name="passwordConfirm"
                      type="password"
                      label="Repita sua senha"
                      value={modalFormik.values.passwordConfirm}
                      error={modalFormik.errors.passwordConfirm}
                      helperText={modalFormik.errors.passwordConfirm}
                      onChange={modalFormik.handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      labelPlacement='end'
                      label={
                        <Typography color='primary'>
                          Estou ciente que esta ação não poderá ser desfeita
                        </Typography>
                      }
                      control={
                        <Checkbox
                          checked={modalFormik.values.actionConsent}
                          onChange={event => modalFormik.setFieldValue('actionConsent', event.target.checked)}
                        />
                      }
                    />
                    {!!modalFormik.errors.actionConsent && (
                      <FormHelperText error>
                        {modalFormik.errors.actionConsent}
                      </FormHelperText>
                    )}
                  </Grid>
                  <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                    <Button
                      type="button"
                      variant="outlined"
                      color="tertiary"
                      onClick={() => setIsModalOpen(false)}
                    >
                      Voltar
                    </Button>
                    <Button
                      type="submit"
                      color="success"
                      loading={isLoading}
                    >
                      Remover
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Grid>
          </Grid>
        </Dialog>
      )}
      <Grid xs={12}>
        <Typography color="primary" variant="subtitle2" style={{ borderBottom: '1px solid #E4E7EA' }}>
          Forçar Saída do Veículos
        </Typography>
      </Grid>
      <Grid xs={12}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" style={{ marginTop: 30 }}>
                Buscar OSs que deram entrada a partir da data:
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  inputFormat="DD/MM/YYYY"
                  label="Data início"
                  value={formik.values.startDate}
                  onChange={date => formik.setFieldValue('startDate', moment(date).format('YYYY-MM-DD'))}
                  renderInput={params => (
                    <TextField
                      {...params}
                      fullWidth
                      name="startDate"
                      variant='outlined'
                      error={formik.errors.startDate}
                      helperText={formik.errors.startDate}
                      inputProps={{
                        ...params.inputProps
                      }}
                      SelectProps={{
                        MenuProps: {
                          disableEnforceFocus: true
                        }
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Tooltip
                placement="top-start"
                title={
                  <Typography>
                    Essa data/hora também será usada como data de pagamento e entrada das novas OSs.
                  </Typography>
                }
              >
                <Typography variant="subtitle2">
                  Data e hora de saída de TODAS as OSs?
                </Typography>
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  inputFormat="DD/MM/YYYY"
                  label="Data fim"
                  value={formik.values.endDate}
                  onChange={date => formik.setFieldValue('endDate', moment(date).format('YYYY-MM-DD'))}
                  renderInput={params => (
                    <TextField
                      {...params}
                      fullWidth
                      name="endDate"
                      variant='outlined'
                      error={formik.errors.endDate}
                      helperText={formik.errors.endDate}
                      inputProps={{
                        ...params.inputProps
                      }}
                      SelectProps={{
                        MenuProps: {
                          disableEnforceFocus: true
                        }
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={3}>
              <Input
                type='timeRangePadStart'
                name='endTime'
                label='Hora'
                value={formik.values.endTime}
                error={formik.errors.endTime}
                helperText={formik.errors.endTime}
                onChange={event => {
                  if(event.target.value.length > 5) {
                    return;
                  }

                  if(hoursAndMinutesToMinutes(event.target.value) > 1439) {
                    return;
                  }

                  formik.setFieldValue('endTime', event.target.value);
                }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2">
                Escolha um meio de pagamento para dar saída nos veículos não pagos
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Select
                name="paymentMethodId"
                label="Meio de Pagamento"
                options={paymentMethods}
                value={formik.values.paymentMethodId}
                error={formik.errors.paymentMethodId}
                helperText={formik.errors.paymentMethodId}
                onChange={formik.handleChange}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2">
                Deseja dar saída e entrada nos veículos
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Select
                name="entryType"
                label="Entrada ou saída"
                options={entryType}
                value={formik.values.entryType}
                error={formik.errors.entryType}
                helperText={formik.errors.entryType}
                onChange={formik.handleChange}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                style={{ margin: 0 }}
                label={
                  <Typography variant="subtitle2">
                    <b>Dar saída com valor de estacionamento:</b>
                  </Typography>
                }
                labelPlacement="start"
                control={
                  <Switch
                    color="primary"
                    name="exitWithAmount"
                    checked={formik.values.exitWithAmount}
                    onChange={event => formik.setFieldValue(event.target.name, event.target.checked ? 1 : 0)}
                  />
                }
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button
                type="submit"
                color="success"
              >
                Avançar
              </Button>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
}